import { Injectable } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Subject, firstValueFrom, lastValueFrom, of } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { ExtendedFile } from '../../models/extended-file.interface';
import { formatString, jsonToSafeString } from '../../utils/string-utils';

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private missionToken: string | null = null;
  private cancelUpload$ = new Subject<void>(); // RxJS Subject for cancellation
  broadcastEvent: EventEmitter<{ type: string; data: any }> = new EventEmitter();
  private autoIncrementFields: any[] = [];
  private autoFills: any[] = [];
  supportedPreviewTypes:string[] = ["image/webp", "image/png", "image/jpg", "image/gif", "image/bmp", "image/jpeg"];

  
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private userService: UserService,
    private alertsService: AlertsService
  ) {
	  this.setAutoIncrementFields();
	  this.autoFills = this.configService.getConfig().upload?.autoFill || [];
  }

  async upload(uploadFiles: ExtendedFile[], metadata: any): Promise<void> {
    if (!uploadFiles?.length) {
      return Promise.reject('No files to upload');
    }

    // Store upload info in localStorage
    if (localStorage.getItem("upload")) {
    	this.saveUploadState(uploadFiles, true);
    }

    try {
      for (const file of uploadFiles) {
        if ((file as any).ready === 'OVERLIMIT') {
			return Promise.reject('File not ready '+file);
		} 
        this.updateUploadState(file, 'PENDING');
        await this.uploadSingleFile(file);
        this.updateUploadState(file, 'SUCCESS');
      }
      // After uploading all files, trigger ingestion
      await this.ingestFiles(metadata);
    } catch (error) {
      console.error('Upload Error:', error);
      this.alertsService.error({
        title: 'Upload Failed',
        message: 'The upload failed to complete.'
      });
    } finally {
      this.saveUploadState([], false); // Clear localStorage
    }
  }

  private async uploadSingleFile(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('productFile', file, file.name);
    if (this.missionToken) {
      formData.append('missionToken', this.missionToken);
    }
    const url = this.configService.getConfig().upload.uploadSingleURL;
    this.broadcastEvent.emit({ type:"fileStatus", data:{ file: file, ready: 'PENDING' }});    
    try {
     const response = await lastValueFrom(this.http.post<string>(url, formData, { responseType: 'text' as 'json' }));   
     this.missionToken = response;
     this.updateUploadState(file, 'SUCCESS');
     this.broadcastEvent.emit({ type:"fileStatus", data:{ file: file, ready: 'SUCCESS' }});
    } catch (error) {
     this.updateUploadState(file, 'FAIL');
     this.broadcastEvent.emit({ type:"fileStatus", data:{ file: file, ready: 'FAIL' }});
     console.log("Failed uploading one file");
     throw error;
    }
  }
  
  setBroadcastEvent(type: string, data: any){
	  this.broadcastEvent.emit({ type, data});
  }
  
  private async ingestFiles(metadata: any): Promise<void> {
    const formData = new FormData();
    formData.append('missionToken', this.missionToken!);
    formData.append('libraryName', metadata.library);
    formData.append('addWorkflow', 'PACKAGE');
    formData.append('detectWorkflow', 'PACKAGE');
    formData.append('customMetadata', jsonToSafeString(JSON.stringify(metadata.customMetadata)));
    formData.append("runOCR", metadata.runOCR);
    formData.append("skipDuplicateCheck", metadata.skipDuplicateCheck);
    formData.append("isSkipHeaderMetadtaExtraction", metadata.isSkipHeaderMetadtaExtraction);
    if (metadata.rmCategory) formData.append("rmCategory", metadata.rmCategory);
    if (metadata.rmSubcategory) formData.append("rmSubcategory", metadata.rmSubcategory);
    if (metadata.rmFolder) formData.append("rmFolder", metadata.rmFolder);

    const url = this.configService.getConfig().upload.ingestSingleURL;
    this.broadcastEvent.emit({ type:"ingestStatus", data: { status: "info", message: "Starting upload..." }})
    try {
       const response: any = await this.http.post(url, formData)
        .pipe(takeUntil(this.cancelUpload$)) // Cancel if triggered
        .toPromise();
      this.broadcastEvent.emit({ type:"uploadFinished",data:{}});
      if (response.numberOfFailedFiles > 0) {
		this.broadcastEvent.emit({ type:"ingestStatus", data: { status: "warn", message: "Finished uploading files with errors." }});  
        this.alertsService.error({
          title: this.userService.LOCALIZATION.uploadFinished,
          message: this.userService.LOCALIZATION.uploadError
        });
      } else {
		this.broadcastEvent.emit({ type:"ingestStatus", data:{ status: "success", message: "Finished uploading files." }});
        this.alertsService.success({
          title: this.userService.LOCALIZATION.uploadFinished,
          message: this.userService.LOCALIZATION.uploadSuccess
        });
      }
    } catch (error) {
	  this.broadcastEvent.emit({ type:"ingestStatus", data:{ status: "fail", message: "Could not upload files." }});	
      console.error('Ingestion Error:', error);
      throw error;
    }
  }

 private saveUploadState(files: ExtendedFile[], uploading: boolean): void {
  if (!Array.isArray(files)) {
    console.warn("Invalid files array provided to saveUploadState.");
    return;
  }
  const uploadState = files.length > 0 ? {
      uploadFiles: files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        ready: ''
      })),
      uploading: uploading
    } : { uploadFiles: [], uploading: uploading };

  try {
    localStorage.setItem("upload", JSON.stringify(uploadState));
  } catch (error) {
    console.error("Failed to save upload state:", error);
  }
 }

 private updateUploadState(file: File, status: string): void {
    const storedUpload = localStorage.getItem("upload");
    if (storedUpload) {
      let upload = JSON.parse(storedUpload);
      let fileEntry = upload.uploadFiles.find((f: any) => f.name === file.name);
      if (fileEntry) {
        fileEntry.ready = status;
        try {
          localStorage.setItem("upload", JSON.stringify(upload));
        } catch (error) {
          console.error("Failed to save upload state:", error);
        }
      }
    }
 }

 async setAutoIncrementFields():Promise<void>{
	 try {
	   const url = this.configService.getConfig().upload.incrementFieldConfigURL;
	   const response: any[] = (await firstValueFrom(this.http.get<any[]>(url))) ?? [];
	   this.autoIncrementFields = response ? response : [];
	 } catch (error) {
       console.error("Failed to get upload AutoIncrementFields:", error);
       this.autoIncrementFields = [];
     }
 }
 
  getAutoFillForLibrary(libraryName: string): any | null {
    const autoFillField = this.autoFills.find(filler => {
      const fillLibraries: string[] = filler.libraries || [];
      return fillLibraries.includes(libraryName);
    });

    if (autoFillField) {
      const clone = { ...autoFillField };

      // Convert `ignorable` to an array if it's a string
      if (typeof clone.ignorable === 'string') {
        clone.ignorable = clone.ignorable.split(',');
      } else {
        clone.ignorable = clone.ignorable || [];
      }

      // Always skip these fields when filling the form
      clone.ignorable.push('lb_document_id', 'load_date', 'uuid', 'product_size');

      // Find user field based on searchField
      const userField = this.userService.user?.properties?.awfieldObjectList?.find(
        (x: any) => x.name === clone.searchField
      );

      if (userField) {
        clone.searchFieldLabel = userField.displayName || clone.searchField;
      }

      return clone;
    }

    return null;
  }
  
  async ingest(uploadFiles: ExtendedFile[], metadata: any): Promise<void> {
    if (!uploadFiles) {
      return Promise.reject('No files to upload.');
    }

    this.saveUploadState(uploadFiles, true);

    let missionToken: string | null = null;
    
    // Auto-fill metadata if applicable
    const autoField = this.autoIncrementFields.find(o => o.libraryName === metadata.library);
    if (autoField) {
      metadata.customMetadata[autoField.fieldName] = [autoField.fieldMarkerValue];
    }

    try {
      for (const file of uploadFiles) {
		try {  
	        if ((file as any).ready === 'OVERLIMIT') {
	          return Promise.reject(`File over limit: ${file.name}`);
	        }
	
	        this.broadcastEvent.emit({type: "fileStatus", data: {file, ready: 'PENDING'}});
	        this.updateUploadState(file, 'PENDING');
	
	        const response: any = await this.ingestSingleFile(file, metadata);
	        if (response?.numberOfFailedFiles > 0 || response?.numberOfUnknownFiles > 0) {
	          this.handleUploadFailure(file, response);
	          this.broadcastEvent.emit({ type:"ingestStatus", data: { status: "warn", message: "Finished uploading files with errors." }});
	        } else {
	          this.broadcastEvent.emit({ type:"fileStatus", data:{file, ready: 'SUCCESS'}});
	          this.updateUploadState(file, 'SUCCESS');
	          this.broadcastEvent.emit({ type:"ingestStatus", data:{ status: "success", message: "Finished uploading files." }});
	        }
	    } catch (error) {
	        console.error('Upload Error for file:', file?.name, error);
	        this.broadcastEvent.emit({type:"fileStatus", data:{ file, ready: 'FAIL', message: "Upload failed." }});
	        this.updateUploadState(file, 'FAIL');
	    }
      }
    }  finally {
      this.saveUploadState([], false);
    }
 }
 
 async ingestSingleFile(file: File, metadata: any): Promise<any> {
    const formData = new FormData();
    formData.append("productFile", file, file.name);
    formData.append("libraryName", metadata.library);
    formData.append("addWorkflow", "DEFAULT");
    formData.append("detectWorkflow", "DEFAULT");
    formData.append("runOCR", metadata.runOCR);
    formData.append("skipDuplicateCheck", metadata.skipDuplicateCheck);
    formData.append("isSkipHeaderMetadtaExtraction", metadata.isSkipHeaderMetadtaExtraction);
    formData.append("customMetadata", jsonToSafeString(JSON.stringify(metadata.customMetadata)));

    if (metadata.rmCategory) formData.append("rmCategory", metadata.rmCategory);
    if (metadata.rmSubcategory) formData.append("rmSubcategory", metadata.rmSubcategory);
    if (metadata.rmFolder) formData.append("rmFolder", metadata.rmFolder);
    if ('thumbnail' in file && file.thumbnail instanceof File) {
      formData.append("thumbnail", file.thumbnail, file.thumbnail.name);
    }

    const url = this.configService.getConfig().upload.ingestSingleURL;
    const headers = new HttpHeaders().set('Accept', 'application/json');

    return this.http.post(url, formData, { headers })
      .pipe(
        takeUntil(this.cancelUpload$), // Cancel if triggered
        map(response => JSON.parse(JSON.stringify(response))), // Ensure response parsing
        catchError(error => {
          console.error("File upload failed:", error);
          throw error;
        })
      ).toPromise();
 }
 private handleUploadFailure(file: File, response: any): void {
    let message = "";
    const successFiles: any[] = [];
    const failedFiles: any[] = [];
    if (response.description && response.description.indexOf('License validation error') > -1) {
      this.broadcastEvent.emit({ type:"fileStatus", data: { file: file, ready: 'FAILLICENSE', data: response, successFiles: [], failedFiles: [] }});
    } else { 
     if (response.detailedLogs && response.detailedLogs.length > 0) {
      response.detailedLogs.forEach((log: any) => {
        const fileName = log.filePath.split("\\").pop();
        if (log.state === "FAILED") {
          failedFiles.push({ name: fileName, message: log.message });
        } else {
          successFiles.push({ name: fileName });
        }
      });
      
      /*message += `Attempted to load ${response.numberOfFilesFound} files.\n`;
      message += `${response.numberOfFilesAdded} files added.\n`;
      if (response.numberOfFailedFiles > 0) {
        message += `${response.numberOfFailedFiles} failed files.\n`;
      }
      if (response.numberOfUnknownFiles > 0) {
        message += `${response.numberOfUnknownFiles} unknown files.\n`;
      }*/
      this.broadcastEvent.emit({type:"fileStatus", data:{ file: file, ready: 'FAIL', message: message, successFiles: successFiles, failedFiles: failedFiles }});
     }
    }
    this.updateUploadState(file, 'FAIL');
 }
 
 formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
 }
retrievePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!this.supportedPreviewTypes.includes(file.type)) {
      return reject("unsupported");
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.result);
    reader.readAsDataURL(file);
  });
}

loadThumbnail(file: File, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    this.broadcastEvent.emit({ type: "thumbLoadStatus", data: { file, ready: "PENDING" } });

    const formData = new FormData();
    formData.append("thumbnailFile", file, file.name);

	let url = formatString(this.configService.getConfig().upload.loadThumbnailURL, id);
    const request$ = this.http.post<{ numberOfFailedFiles: number; numberOfUnknownFiles: number; description?: string; detailedLogs?: any[] }>(url, formData);

    const subscription = request$.pipe(
      takeUntil(this.cancelUpload$) // Cancel request when cancelUpload$ emits
    ).subscribe({
      next: (data) => {
        if (data.numberOfFailedFiles > 0 || data.numberOfUnknownFiles > 0) {
          let message = data.detailedLogs?.[0]?.message || "Unknown error";
          if (data.description?.includes("License validation error")) {
            this.broadcastEvent.emit({ type: "thumbLoadStatus", data: { file, ready: "FAILLICENSE" } });
          } else {
            this.broadcastEvent.emit({ type: "thumbLoadStatus", data: { file, ready: "FAIL", message } });
          }
          reject(message);
        } else {
          this.broadcastEvent.emit({ type: "thumbLoadStatus", data: { file, ready: "SUCCESS" } });
          resolve();
        }
      },
      error: () => {
        this.broadcastEvent.emit({ type: "thumbLoadStatus", data: { file, ready: "FAIL" } });
        reject("Thumbnail upload failed");
      }
    });

    // Handle cancellation
    this.cancelUpload$.subscribe(() => {
      subscription.unsubscribe();
      this.broadcastEvent.emit({ type: "thumbLoadStatus", data: { file, ready: "CANCELLED" } });
      reject("Thumbnail upload cancelled");
    });
  });
 }
 
 validateIngest(files: string[], addWorkflow: string, isConvertToPDF: boolean): Promise<any[]>{
  let params = new HttpParams()
   .set('files', encodeURIComponent(files.join(',')))
   .set('addWorkflow', addWorkflow)
   .set('isConvertToPDF', isConvertToPDF.toString());
  const url = this.configService.getConfig().upload.ingestValidateURL;
  return lastValueFrom( this.http.get<any[]>(url, { params }).pipe(
    catchError(err => {
      console.error("Error validating ingest:", err);
      return of([]); // Return empty array if request fails
    })
   ))
 }
 
  getNotificationFields(metadata: any): Promise<any> {
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(metadata.customMetadata));
    formData.append('libraryName', metadata.library);

    return lastValueFrom(this.http
      .post<any>(this.configService.getConfig().upload.uploadNotificationMetadataURL, formData)
      .pipe(
        catchError((error) => {
          console.error('Error fetching notification fields:', error);
          return of(false); // Return false in case of error
        })
      ));
  }
  
 cancelUpload(): void {
    this.cancelUpload$.next(); // Emit cancel signal
    //this.cancelUpload$.complete(); // Close Subject
    this.saveUploadState([], false);
    console.log('Upload cancelled.');
  }
}
